import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Building2, MapPin, Check, ArrowRight, Dumbbell, Plus } from 'lucide-react';
import { useGym } from '@/contexts/GymContext';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';

export default function OwnerGyms() {
    const { gyms, selectedGym, setSelectedGym } = useGym();
    const { user, fetchUser } = useAuth();
    const navigate = useNavigate();

    const [dialogOpen, setDialogOpen] = useState(false);
    const [form, setForm] = useState({ name: '', address: '', phone: '', email: '' });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    const maxGyms = user?.subscription?.plan?.max_gyms ?? 0;
    const currentGyms = gyms.length;
    const canAddMore = maxGyms === 0 || currentGyms < maxGyms;
    const isLifetimeFree = user?.subscription?.is_lifetime;

    const handleSelect = (gym) => {
        setSelectedGym(gym);
        navigate('/panel');
    };

    const openCreate = () => {
        setForm({ name: '', address: '', phone: '', email: '' });
        setError('');
        setDialogOpen(true);
    };

    const handleCreate = async (e) => {
        e.preventDefault();
        setSaving(true);
        setError('');
        try {
            await api.post('/owner/gyms', form);
            await fetchUser();
            setDialogOpen(false);
        } catch (err) {
            setError(err.response?.data?.message || 'Error al crear el gimnasio.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div>
                    <h1 className="text-2xl font-bold uppercase tracking-wide text-white">Mis Gimnasios</h1>
                    <p className="text-white/60 text-sm mt-1">
                        Seleccioná el gimnasio que querés gestionar.
                    </p>
                </div>
                {(canAddMore || isLifetimeFree) && (
                    <Button onClick={openCreate} className="w-full sm:w-auto">
                        <Plus className="h-4 w-4 mr-2" />Nuevo Gimnasio
                    </Button>
                )}
            </div>

            {maxGyms > 0 && (
                <p className="text-xs text-white/50 uppercase tracking-wider">
                    {currentGyms} / {maxGyms} gimnasios usados
                </p>
            )}

            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {gyms.map(gym => {
                    const isSelected = selectedGym?.id === gym.id;
                    return (
                        <Card
                            key={gym.id}
                            className={`cursor-pointer transition-all bg-[#0a0a0a] border-[#3a3a3f] ${
                                isSelected ? 'ring-2 ring-white border-white' : ''
                            }`}
                            onClick={() => handleSelect(gym)}
                        >
                            <CardHeader className="pb-2">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        {gym.logo ? (
                                            <img
                                                src={`/storage/${gym.logo}`}
                                                alt={gym.name}
                                                className="h-10 w-10 object-contain rounded-lg border border-[#3a3a3f] bg-white"
                                            />
                                        ) : (
                                            <div className="h-10 w-10 bg-white rounded-lg flex items-center justify-center">
                                                <Dumbbell className="h-5 w-5 text-black" />
                                            </div>
                                        )}
                                        <div>
                                            <CardTitle className="text-base text-white">{gym.name}</CardTitle>
                                            {gym.address && (
                                                <p className="text-xs text-white/60 flex items-center gap-1">
                                                    <MapPin className="h-3 w-3" />
                                                    {gym.address}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                    {isSelected && (
                                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-white text-black shrink-0">
                                            <Check className="h-3 w-3 mr-1" />
                                            Activo
                                        </span>
                                    )}
                                </div>
                            </CardHeader>
                            <CardContent className="pt-0">
                                <div className="flex items-center gap-2 text-sm text-white/60">
                                    <Building2 className="h-4 w-4" />
                                    <span>ID: {gym.id}</span>
                                </div>
                                <Button
                                    variant={isSelected ? 'default' : 'outline'}
                                    size="sm"
                                    className="w-full mt-3"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleSelect(gym);
                                    }}
                                >
                                    {isSelected ? (
                                        <>Gestionando <ArrowRight className="h-4 w-4 ml-1" /></>
                                    ) : (
                                        <>Seleccionar <ArrowRight className="h-4 w-4 ml-1" /></>
                                    )}
                                </Button>
                            </CardContent>
                        </Card>
                    );
                })}
            </div>

            {gyms.length === 0 && (
                <div className="text-center py-12">
                    <Building2 className="h-12 w-12 text-white/60 mx-auto mb-4" />
                    <p className="text-lg font-medium text-white">No tenés gimnasios creados.</p>
                    <p className="text-sm text-white/60 mt-1">
                        Creá tu primer gimnasio para empezar a gestionarlo.
                    </p>
                    {(canAddMore || isLifetimeFree) && (
                        <Button onClick={openCreate} className="mt-4">
                            <Plus className="h-4 w-4 mr-2" />Crear Gimnasio
                        </Button>
                    )}
                </div>
            )}

            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="bg-[#0a0a0a] border-hairline-dark">
                    <DialogHeader>
                        <DialogTitle>Nuevo Gimnasio</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleCreate} className="space-y-4">
                        {error && <div className="bg-[#0a0a0a] border border-hairline-dark text-white text-sm p-3 rounded-md">{error}</div>}
                        <div className="space-y-2">
                            <Label>Nombre</Label>
                            <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required />
                        </div>
                        <div className="space-y-2">
                            <Label>Dirección</Label>
                            <Input value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} />
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Teléfono</Label>
                                <Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} />
                            </div>
                            <div className="space-y-2">
                                <Label>Email</Label>
                                <Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
                            </div>
                        </div>
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
                            <Button type="submit" disabled={saving}>{saving ? 'Creando...' : 'Crear'}</Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
}
